import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index.tsx";
import Insights from "./pages/Insights.tsx";
import LegislationPipeline from "./pages/LegislationPipeline.tsx";
import Events from "./pages/Events.tsx";
import AboutUs from "./pages/AboutUs.tsx";
import NotFound from "./pages/NotFound.tsx";
import Admin from "./pages/Admin.tsx";
import DataTools from "./pages/DataTools.tsx";
import Auth from "./pages/Auth.tsx";
import AuthMfa from "./pages/AuthMfa.tsx";
import IdeasBoard from "./pages/IdeasBoard.tsx";
import IdeaDetail from "./pages/IdeaDetail.tsx";
import ArticleDetail from "./pages/ArticleDetail.tsx";
import Archive from "./pages/Archive.tsx";
import CuratedPostDetail from "./pages/CuratedPostDetail.tsx";
import Profile from "./pages/Profile.tsx";
import PublicProfile from "./pages/PublicProfile.tsx";
import Status from "./pages/Status.tsx";
import ProjectDetail from "./pages/ProjectDetail.tsx";
import AffiliatesLanding from "./pages/AffiliatesLanding.tsx";
import AffiliatesDirectory from "./pages/AffiliatesDirectory.tsx";
import AffiliateProfile from "./pages/AffiliateProfile.tsx";
import AffiliateApply from "./pages/AffiliateApply.tsx";
import AffiliateDashboard from "./pages/AffiliateDashboard.tsx";
import AccountSettings from "./pages/AccountSettings.tsx";
import AccountBilling from "./pages/AccountBilling.tsx";
import Pricing from "./pages/Pricing.tsx";
import Sponsorships from "./pages/Sponsorships.tsx";
import AdminSponsors from "./pages/AdminSponsors.tsx";
import Coach from "./pages/Coach.tsx";
import Opportunities from "./pages/Opportunities.tsx";
import FunderDetail from "./pages/FunderDetail.tsx";
import PartnerHub from "./pages/PartnerHub.tsx";
import PartnerInviteAccept from "./pages/PartnerInviteAccept.tsx";
import Partners from "./pages/Partners.tsx";
import PartnerApply from "./pages/PartnerApply.tsx";
import PartnerHandbook from "./pages/PartnerHandbook.tsx";
import PartnersDirectory from "./pages/PartnersDirectory.tsx";
import Welcome from "./pages/Welcome.tsx";
import Services from "./pages/Services.tsx";
import ForFunders from "./pages/ForFunders.tsx";
import ForAgencies from "./pages/ForAgencies.tsx";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy.tsx";
import TermsOfService from "./pages/legal/TermsOfService.tsx";
import CookiePolicy from "./pages/legal/CookiePolicy.tsx";
import AcceptableUse from "./pages/legal/AcceptableUse.tsx";
import RetentionPolicy from "./pages/legal/RetentionPolicy.tsx";
import DataProcessingAgreement from "./pages/legal/DataProcessingAgreement.tsx";
import SecurityCommitments from "./pages/legal/SecurityCommitments.tsx";
import IncidentResponse from "./pages/legal/IncidentResponse.tsx";
import AccessGate from "./components/AccessGate";
import WelcomeTour from "./components/onboarding/WelcomeTour";
import AnalyticsRouteTracker from "./components/AnalyticsRouteTracker";
import AnalyticsAuthBridge from "./components/AnalyticsAuthBridge";
import SeasonalThemeProvider from "./components/seasonal/SeasonalThemeProvider";
import FeatureFlagRoute from "./components/FeatureFlagRoute";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <SeasonalThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <AnalyticsAuthBridge />
          <AccessGate>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<ArticleDetail />} />
            <Route path="/legislation" element={<LegislationPipeline />} />
            <Route path="/events" element={<Events />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/services" element={<Services />} />
            <Route path="/for-funders" element={<ForFunders />} />
            <Route path="/for-agencies" element={<ForAgencies />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partners/directory" element={<PartnersDirectory />} />
            <Route path="/partners/apply" element={<PartnerApply />} />
            <Route path="/partners/handbook" element={<PartnerHandbook />} />
            <Route path="/data-tools" element={<DataTools />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/mfa" element={<AuthMfa />} />
            <Route path="/ideas" element={<IdeasBoard />} />
            <Route path="/ideas/category/:slug" element={<IdeasBoard />} />
            <Route path="/ideas/tag/:tag" element={<IdeasBoard />} />
            <Route path="/ideas/:ideaId" element={<IdeaDetail />} />
            <Route path="/archive" element={<Archive />} />
            <Route path="/archive/curated/:slug" element={<CuratedPostDetail />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/funders/:slug" element={<FunderDetail />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:userId" element={<PublicProfile />} />
            <Route path="/status" element={<Status />} />
            <Route path="/projects/:projectId" element={<FeatureFlagRoute flag="coach"><ProjectDetail /></FeatureFlagRoute>} />
            <Route path="/coach" element={<FeatureFlagRoute flag="coach"><Coach /></FeatureFlagRoute>} />
            <Route path="/partner" element={<FeatureFlagRoute flag="partnerHub"><PartnerHub /></FeatureFlagRoute>} />
            <Route path="/partner/invite/:token" element={<PartnerInviteAccept />} />
            <Route path="/affiliates" element={<FeatureFlagRoute flag="affiliates"><AffiliatesLanding /></FeatureFlagRoute>} />
            <Route path="/affiliates/directory" element={<FeatureFlagRoute flag="affiliates"><AffiliatesDirectory /></FeatureFlagRoute>} />
            <Route path="/affiliates/apply" element={<FeatureFlagRoute flag="affiliates"><AffiliateApply /></FeatureFlagRoute>} />
            <Route path="/affiliate/dashboard" element={<FeatureFlagRoute flag="affiliates"><AffiliateDashboard /></FeatureFlagRoute>} />
            <Route path="/affiliates/:slug" element={<FeatureFlagRoute flag="affiliates"><AffiliateProfile /></FeatureFlagRoute>} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/account/billing" element={<AccountBilling />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/sponsorships" element={<Sponsorships />} />
            <Route path="/admin/sponsors" element={<AdminSponsors />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/retention" element={<RetentionPolicy />} />
            <Route path="/dpa" element={<DataProcessingAgreement />} />
            <Route path="/security" element={<SecurityCommitments />} />
            <Route path="/incident-response" element={<IncidentResponse />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
            <WelcomeTour />
          </AccessGate>
        </BrowserRouter>
      </TooltipProvider>
      </SeasonalThemeProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
